import {IIntegerRange, IRect, LengthUnit} from '@colibrio/colibrio-reader-framework/colibrio-core-base';
import {ILocator, ILocatorData} from '@colibrio/colibrio-reader-framework/colibrio-core-locator';
import {
    ContentBlockTargetFetchMode,
    CustomReaderViewContentLayout,
    IContentLocation,
    IFetchNavigationItemReferencesResult,
    IFocusOnReadingPositionOptions,
    IPageProgressionTimeline,
    IPublicationStylePalette,
    IReaderDocument,
    IReaderPublication,
    IReaderPublicationNavigationItemReference,
    IReaderView,
    IReaderViewAnnotationMouseEngineEvent,
    IReaderViewGotoOptions, IReaderViewOptions,
    IReaderViewTransformManager,
    IRenderer,
    IRendererOptions, ISwipeNavigationGestureProgressEngineEvent,
    ITransformAnimationOptions,
    ITransformData,
    ITransformRectZoomOptions,
    IUnresolvedContentLocation,
    IVisibleContentRect,
    IVisibleContentRectData,
    NavigationCollectionType,
    ReaderViewGestureType,
    TransformEasing,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import {ResponsiveViewRule} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-engine';
import {
    FlipBookRenderer,
    IPaginatedRendererOptions,
    SingleDocumentScrollRenderer,
    SinglePageSwipeRenderer,
    SpreadSwipeRenderer,
    StackRenderer,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-renderer';
import {IVanillaReaderOptionsDataStore} from '../VanillaReaderDataStore/IVanillaReaderOptionsDataStore';
import {VanillaReaderAnnotationLayerBookmarks} from './VanillaReaderAnnotationLayerBookmarks';
import {VanillaReaderAnnotationLayerHighlights} from './VanillaReaderAnnotationLayerHighlights';
import {VanillaReaderEventBus,} from '../VanillaReaderEventBus/VanillaReaderEventBus';
import {
    IVanillaReaderBookmarkData,
    IVanillaReaderContentPositionData,
    IVanillaReaderHighlightData,
    IVanillaReaderNavigationItem, IVanillaReaderPublicationStylePalette,
    IVanillaReaderReadingProgressionTimeline,
    IVanillaReaderSyntheticPageListData, IVanillaReaderTransformData,
    IVanillaReaderViewOptions,
    IVanillaReaderVisibleRangeData,
    VanillaActiveRendererChangedEventCallback,
    VanillaBooleanValueCallback,
    VanillaPageProgressionTimelineRecalculatedCallback,
    VanillaReaderRendererNames,
    VanillaReaderViewEventCallback,
    VanillaReaderViewOptionsCallback, VanillaSwipeNavigationProgressCallback,
    VanillaVoidCallback,
} from './VanillaReaderModel';
import {VanillaReaderNavigationTree} from './VanillaReaderNavigationTree';
import {VanillaReaderPublication} from './VanillaReaderPublication';
import {VanillaReaderReadingStats} from './VanillaReaderReadingStats';
import {VanillaReaderReadingSystem} from './VanillaReaderReadingSystem';
import {VanillaReaderAnnotationLayerPageMarkers} from './VanillaReaderAnnotationLayerPageMarkers';
import {VanillaReaderUtils} from "./VanillaReaderUtils";
import {ObjectUtils} from "../utils/base/ObjectUtils/ObjectUtils";

/*
* # VanillaReaderView
*
* ## PRIMARY COLIBRIO FRAMEWORK TYPES
*
* - IReaderView
* - IReaderViewAnnotationLayer
* - IReaderPublication
* - IReadingSystemEngine
*
* ## RESPONSIBILITIES
*
* This is a helper wrapper around the Colibrio Reader Framework's `IReaderView` type.
*
* */
export class VanillaReaderView {

    highlightsLayer: VanillaReaderAnnotationLayerHighlights | undefined;
    bookmarksLayer: VanillaReaderAnnotationLayerBookmarks | undefined;
    pageMarkerLayer: VanillaReaderAnnotationLayerPageMarkers | undefined;
    readingStats: VanillaReaderReadingStats | undefined;

    private readonly _colibrioReaderView: IReaderView;
    private _useReducedMotion: boolean = false;
    private _scrollOverflowingTables: boolean = false;
    private _ignoreAspectRatio: boolean = false;
    private _showPageMarkerLayer: boolean = false;
    private _initialVanillaReaderViewOptions: IVanillaReaderViewOptions | undefined;
    private _defaultAnimationDuration: number = 600;
    private _activePalette: IPublicationStylePalette | undefined | null;
    private _onReaderViewCreatedCallback: VanillaReaderViewOptionsCallback | undefined;
    private _onReducedMotionValueChangedCallback: VanillaBooleanValueCallback | undefined;
    private _onIgnoreAspectRatioValueChangedCallback: VanillaBooleanValueCallback | undefined;
    private _onActiveRendererChangedCallback: VanillaActiveRendererChangedEventCallback | undefined;
    private _onSwipeNavigationGestureProgressCallback: VanillaSwipeNavigationProgressCallback | undefined;
    private _onRendererTransitionStartedCallback: VanillaReaderViewEventCallback | undefined;
    private _onRendererTransitionEndedCallback: VanillaReaderViewEventCallback | undefined;
    private _onRendererScrollStartedCallback: VanillaReaderViewEventCallback | undefined;
    private _onRendererScrollEndedCallback: VanillaReaderViewEventCallback | undefined;
    private _onPageProgressionTimelineRecalculatedCallback: VanillaPageProgressionTimelineRecalculatedCallback | undefined;
    private _currentVisibleContentRects: IVisibleContentRect[] | undefined = undefined;
    private _activeTransformData: IVanillaReaderTransformData;
    private _initialPublicationViewportRects: IRect[] = [];

    constructor(
        private _vanillaReaderReadingSystem: VanillaReaderReadingSystem,
        private _vanillaReaderPublication: VanillaReaderPublication,
        private _readerViewElement: HTMLElement,
        private _vanillaReaderOptionsDataStore: IVanillaReaderOptionsDataStore,
        private _customContentOnLoading?: string,
        private _customContentOnLoadError?: string,
    ) {
        this._colibrioReaderView = this._vanillaReaderReadingSystem.createReaderView({
        });
        // Now let's set the reader documents to the Colibrio ReaderView instance. Note that this needs to be done in the
        // sync constructor in order for the calling code to execute correctly.
        this._colibrioReaderView.setReaderDocuments(this._vanillaReaderPublication.getColibrioReaderPublication().getSpine());

        this._activeTransformData = this._setActiveTransformData({
            transformData: undefined,
            zoomTargetClientRect: undefined,
            visiblePageClientRects: [],
            publicationViewportScale: 1
        });

    }

    public get activeTransformData(): IVanillaReaderTransformData {
        return this._activeTransformData;
    }

    async setup(): Promise<void> {

        // Let's get the current option's data from the store. If we find nothing in the store, we set the initial
        // options from the defaults provided by the `getOptions` method.
        let initialOptionsData = await this._vanillaReaderOptionsDataStore.fetchVanillaReaderOptionsData();
        this._initialVanillaReaderViewOptions = initialOptionsData?.viewOptions ?
            initialOptionsData.viewOptions : this.getCurrentVanillaReaderViewOptionsState();

        this._activePalette = this._initialVanillaReaderViewOptions?.colibrioReaderViewOptions?.publicationStyleOptions?.palette;

        this.showPageMarkerLayer = this._initialVanillaReaderViewOptions.showPageRangesLayer || false;

        this._createRenderers(this._colibrioReaderView);

        // Add custom content to be shown while the reader view is loading new content.
        this._colibrioReaderView.setContentOnLoading({
            layout: CustomReaderViewContentLayout.FLEX_CENTER,
            renderTo: (containerElement: HTMLElement) => {
                containerElement.innerHTML = this._customContentOnLoading || 'loading...';
            },
        });

        // Add custom content to be shown if a loading error occurs.
        this._colibrioReaderView.setContentOnLoadError({
            layout: CustomReaderViewContentLayout.FLEX_CENTER,
            renderTo: (containerElement: HTMLElement) => {
                containerElement.innerHTML = this._customContentOnLoadError || 'An error occurred';
            },
        });

        // We check the this._initialVanillaReaderViewOptions object to see if the user has a saved preference for which renderer to use. This
        // will override the renderers added above by explicitly calling `setActiveRenderer()` and their respective
        // `ResponsiveViewRules`.
        if (this._initialVanillaReaderViewOptions && this._initialVanillaReaderViewOptions?.activeRendererTypeName) {

            // We have an option in the `VanillaReaderRendererNames` called `RESPONSIVE`. This option is not really the
            // name of a renderer instance, but is a flag that we should let the `IReaderView` select a renderer for us.
            if (this._initialVanillaReaderViewOptions?.activeRendererTypeName !== VanillaReaderRendererNames.RESPONSIVE) {

                // Since the this._initialVanillaReaderViewOptions has supplied us with a renderer name that does not match `VanillaReaderRendererNames.RESPONSIVE`
                // we can tell the reader view that we will handle selection of renderers manually.
                this._colibrioReaderView.setResponsiveRendererSelectionEnabled(false);

                let newActiveRenderer = this._colibrioReaderView.getRendererByName(this._initialVanillaReaderViewOptions?.activeRendererTypeName);
                if (newActiveRenderer) {
                    this._colibrioReaderView.setActiveRenderer(newActiveRenderer);
                } else {
                    console.warn('VanillaReader.createReaderView()', 'Unable to set new active renderer "' + this._initialVanillaReaderViewOptions?.activeRendererTypeName + '" from this._initialVanillaReaderViewOptions?.');
                }

            } else {

                // The this._initialVanillaReaderViewOptions has supplied us with a renderer name that matches `VanillaReaderRendererNames.RESPONSIVE`.
                // Let's tell the reader view that it should use the `ResponsiveViewRules` to determine the best renderer
                // to use.
                this._colibrioReaderView.setResponsiveRendererSelectionEnabled(true);

            }
        }

        // Now that we have things set up we can finally render the Colibrio ReaderView to the HTML element that was
        // passed in to the constructor
        this._colibrioReaderView.renderTo(this._readerViewElement);

        // Add some event listeners to keep track of transition events, such as page turn animation.
        this._colibrioReaderView.addEngineEventListener('rendererTransitionStarted', (evt) => {
            if (this._onRendererTransitionStartedCallback) {
                this._onRendererTransitionStartedCallback(evt);
            }
        });
        this._colibrioReaderView.addEngineEventListener('rendererTransitionEnded', (evt) => {
            if (this._onRendererTransitionEndedCallback) {
                this._onRendererTransitionEndedCallback(evt);
            }
        });
        this._colibrioReaderView.addEngineEventListener('rendererScrollStarted', (evt) => {
            if (this._onRendererScrollStartedCallback) {
                this._onRendererScrollStartedCallback(evt);
            }
        });
        this._colibrioReaderView.addEngineEventListener('rendererScrollEnded', (evt) => {
            if (this._onRendererScrollEndedCallback) {
                this._onRendererScrollEndedCallback(evt);
            }
        });
        this._colibrioReaderView.addEngineEventListener('swipeNavigationGestureProgress', (evt: ISwipeNavigationGestureProgressEngineEvent)=>{
            if(this._onSwipeNavigationGestureProgressCallback) {
                this._onSwipeNavigationGestureProgressCallback(evt.toJSON());
            }
        });

        this._vanillaReaderReadingSystem.onVisibleContentRendered(() => {
            this._event_visibleContentRendered().catch(console.warn);
        });

        this._vanillaReaderReadingSystem.onVisiblePagesChanged(() => {
            this._event_visiblePagesChanged().catch(console.warn);
        });

        this._colibrioReaderView.addEngineEventListener('activeRendererChanged', (evt) => {

            if (evt.newActiveRenderer) {
                let options: IRendererOptions;

                if (evt.newActiveRenderer.getName() !== VanillaReaderRendererNames.SCROLL) {
                    const paginatedRendererOptions: IPaginatedRendererOptions = {
                        disableAnimations: this.useReducedMotion,
                        ignoreAspectRatio: this.ignoreAspectRatio,
                    };
                    options = paginatedRendererOptions;
                } else {
                    options = {
                        ignoreAspectRatio: this.ignoreAspectRatio,
                    };
                }

                evt.newActiveRenderer.setOptions(options);
            }

            if (this._onActiveRendererChangedCallback) {
                this._onActiveRendererChangedCallback(evt);
            }
            this._persistCurrentVanillaReaderOptionsState().catch(console.warn);
        });

        this._colibrioReaderView.addEngineEventListener('pageProgressionTimelineRecalculated', (evt) => {
            if (this._onPageProgressionTimelineRecalculatedCallback) {
                this._onPageProgressionTimelineRecalculatedCallback(evt);
            }
        });

        this._vanillaReaderReadingSystem.onActiveReaderViewTransformChanged(async (transformData: ITransformData | null) => {
            let transformedPublicationClientRects = this.getVisiblePagesBoundingClientRects();
            let publicationViewportScale = this._getPublicationViewportScale(this._initialPublicationViewportRects, transformedPublicationClientRects);

            let vanillaTransformData: IVanillaReaderTransformData | undefined;

            if(transformData) {
                vanillaTransformData = {
                    transformData,
                    visiblePageClientRects:  this.getVisiblePagesBoundingClientRects(),
                    zoomTargetClientRect: this._activeTransformData?.zoomTargetClientRect,
                    publicationViewportScale
                };
            }

            if(vanillaTransformData && transformedPublicationClientRects) {
                vanillaTransformData.visiblePageClientRects = transformedPublicationClientRects;
            }

            if ((transformData && !transformData.animating && !transformData.userGestureActive) || !transformData) {
                let rects = (await this.updateAndFetchVisibleContentRects()) || [];
                let rectData = rects.map(rect => rect.toJSON());

                VanillaReaderEventBus.dispatchEvent(
                    VanillaReaderEventBus.createEvent('VISIBLE_CONTENT_RECTS_CHANGED', {clientRects: rectData || []})
                );

                VanillaReaderEventBus.dispatchEvent(
                    VanillaReaderEventBus.createEvent('PUBLICATION_VIEWPORT_CHANGED', {
                        visiblePageClientRects: transformedPublicationClientRects
                    })
                );

                if (transformData) {
                    VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('VIEW_TRANSFORM_STOPPED', {transformData: vanillaTransformData}));
                } else {
                    vanillaTransformData = undefined;
                    VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('VIEW_TRANSFORM_RESET', {}));
                }
            }

            this._setActiveTransformData(vanillaTransformData);

            VanillaReaderEventBus.dispatchEvent(
                VanillaReaderEventBus.createEvent('VIEW_TRANSFORM_CHANGED', {transformData: this.activeTransformData})
            );
        });

        // Here we create the Annotation Layer that will render all the highlights that the user has added to the book.
        this.highlightsLayer = new VanillaReaderAnnotationLayerHighlights('highlights', 'yellow', this._colibrioReaderView);
        this._setupHighlightLayerCallbacks(this.highlightsLayer);

        // And here we create the Annotation Layer that will render all the bookmarks that the user has added to the book.
        this.bookmarksLayer = new VanillaReaderAnnotationLayerBookmarks('bookmarks', this._colibrioReaderView);
        this._setupBookmarkLayerCallbacks(this.bookmarksLayer);

        // And then we create our simple reading stats instance.
        this.readingStats = new VanillaReaderReadingStats();

        if (this._onReaderViewCreatedCallback) {
            let options = this.getCurrentVanillaReaderViewOptionsState();
            this._onReaderViewCreatedCallback(options);
        }

        this.setVanillaReaderViewOptions(this._initialVanillaReaderViewOptions);

    }

    onReducedMotionValueChanged(callback: VanillaBooleanValueCallback): void {
        this._onReducedMotionValueChangedCallback = callback;
    }

    onIgnoreAspectRatioValueChanged(callback: VanillaBooleanValueCallback): void {
        this._onIgnoreAspectRatioValueChangedCallback = callback;
    }

    onReaderViewCreated(callback: VanillaVoidCallback): void {
        this._onReaderViewCreatedCallback = callback;
    }

    onActiveRendererChanged(callback: VanillaActiveRendererChangedEventCallback): void {
        this._onActiveRendererChangedCallback = callback;
    }

    onSwipeNavigationGestureProgress(callback: VanillaSwipeNavigationProgressCallback): void {
        this._onSwipeNavigationGestureProgressCallback = callback;
    }

    onRendererTransitionStarted(callback: VanillaReaderViewEventCallback): void {
        this._onRendererTransitionStartedCallback = callback;
    }

    onRendererTransitionEnded(callback: VanillaReaderViewEventCallback): void {
        this._onRendererTransitionEndedCallback = callback;
    }

    onRendererScrollStarted(callback: VanillaReaderViewEventCallback): void {
        this._onRendererScrollStartedCallback = callback;
    }

    onRendererScrollEnded(callback: VanillaReaderViewEventCallback): void {
        this._onRendererScrollEndedCallback = callback;
    }

    onPageProgressionTimelineRecalculated(callback: VanillaPageProgressionTimelineRecalculatedCallback): void {
        this._onPageProgressionTimelineRecalculatedCallback = callback;
    }

    public get activePalette(): IVanillaReaderPublicationStylePalette | undefined | null {
        return this._activePalette;
    }

    public get colibrioReaderView(): IReaderView {
        return this._colibrioReaderView;
    }

    setReaderDocuments(documents: IReaderDocument[]): void {
        this._colibrioReaderView.setReaderDocuments(documents);
    }

    getActiveRenderer(): IRenderer | null {
        return this._colibrioReaderView.getActiveRenderer();
    }

    canPerformPrevious(): boolean {
        return this._colibrioReaderView.canPerformPrevious();
    }

    canPerformNext(): boolean {
        return this._colibrioReaderView.canPerformNext();
    }

    canPerformGoTo(): boolean {
        return this._colibrioReaderView.canPerformGoTo();
    }

    previous(): Promise<void> {
        return this._colibrioReaderView.previous();
    }

    next(): Promise<void> {
        return this._colibrioReaderView.next();
    }

    goTo(
        location: ILocator | ILocatorData | IUnresolvedContentLocation | IContentLocation,
        options?: IReaderViewGotoOptions,
    ): Promise<void> | undefined {
        return this._colibrioReaderView.goTo(location, options);
    }

    goToStart(): Promise<void> | undefined {
        return this._colibrioReaderView.goToStart();
    }

    isAtStart(): boolean {
        return this._colibrioReaderView.isAtStart();
    }

    isAtEnd(): boolean {
        return this._colibrioReaderView.isAtEnd();
    }

    isResponsiveRendererSelectionEnabled(): boolean {
        return this._colibrioReaderView.isResponsiveRendererSelectionEnabled();
    }

    getReadingPosition(): IContentLocation | null {
        return this._colibrioReaderView.getReadingPosition();
    }

    focusOnReadingPosition(_options: IFocusOnReadingPositionOptions): Promise<void> {
        return this._colibrioReaderView.focusOnReadingPosition(_options).catch(() => {
            console.log('VanillaReaderView.focusOnReadingPosition() failed');
        });
    }

    clearContentSelection(): void {
        this.colibrioReaderView.clearContentSelection();
    }

    getCurrentVanillaReaderViewOptionsState(): IVanillaReaderViewOptions {
        let publicationStyleOptions = this.colibrioReaderView.getOptions().publicationStyleOptions;
        const rendererName = this.getActiveRenderer()?.getName();
        const activeRendererName = (rendererName && Object.values(VanillaReaderRendererNames).some(v => v === rendererName)) ?
            rendererName as VanillaReaderRendererNames : undefined;
        return {
            colibrioReaderViewOptions: {
                publicationStyleOptions,
            },
            ignoreAspectRatio: this.ignoreAspectRatio,
            useReducedMotion: this.useReducedMotion,
            showPageRangesLayer: this.showPageMarkerLayer,
            scrollOverflowingTables: this.scrollOverflowingTables,
            activeRendererTypeName: this.isResponsiveRendererSelectionEnabled() ?
                VanillaReaderRendererNames.RESPONSIVE : activeRendererName,
            colibrioRendererOptions: this.colibrioReaderView.getActiveRenderer()?.getOptions(),

        };
    }

    setActivePalette(palette: IPublicationStylePalette | null): void {
        this._activePalette = palette;
        this.getActiveRenderer()?.setOptions({
            pageBackgroundColor: palette?.backgroundLight,
        });

        this.pageMarkerLayer?.refresh();
    }

    setVanillaReaderViewOptions(viewOptions: IVanillaReaderViewOptions): void {
        if (viewOptions.colibrioRendererOptions) {
            const activeRendererOptions = this.colibrioReaderView.getActiveRenderer()?.getOptions();
            if(!ObjectUtils.deepEqual(activeRendererOptions, viewOptions.colibrioRendererOptions)) {
                this.colibrioReaderView.getActiveRenderer()?.setOptions(viewOptions.colibrioRendererOptions);
            }
        }

        if (viewOptions.useReducedMotion !== undefined && this.useReducedMotion !== viewOptions.useReducedMotion) {
            this.useReducedMotion = viewOptions.useReducedMotion;
        }
        if (viewOptions.ignoreAspectRatio !== undefined && this.ignoreAspectRatio !== viewOptions.ignoreAspectRatio) {
            this.ignoreAspectRatio = viewOptions.ignoreAspectRatio;
        }
        if (viewOptions.scrollOverflowingTables !== undefined && viewOptions.scrollOverflowingTables !== this.scrollOverflowingTables) {
            this.scrollOverflowingTables = viewOptions.scrollOverflowingTables;
        }

        if (viewOptions.activeRendererTypeName !== undefined && this.activeRendererName !== viewOptions.activeRendererTypeName) {
            this.changeRenderer(viewOptions.activeRendererTypeName, this.ignoreAspectRatio);
        }

        if (viewOptions.colibrioReaderViewOptions) {
            let colibrioReaderViewOptions = this.colibrioReaderView.getOptions();
            const colibrioPublicationStyleOptions = colibrioReaderViewOptions.publicationStyleOptions;
            const publicationStyleOptionsHasChanges = !ObjectUtils.deepEqual(colibrioPublicationStyleOptions, viewOptions.colibrioReaderViewOptions.publicationStyleOptions)

            if(colibrioPublicationStyleOptions && publicationStyleOptionsHasChanges) {
                // Only set the active palette data if it's an  object or if it is explicitly set to null
                const paletteViewOption = viewOptions.colibrioReaderViewOptions.publicationStyleOptions?.palette;
                const activePalette = paletteViewOption !== undefined ? paletteViewOption : null;
                const publicationStyleOptions = viewOptions.colibrioReaderViewOptions.publicationStyleOptions || {};
                publicationStyleOptions.palette = activePalette;

                this.setActivePalette(activePalette);

                this.colibrioReaderView.setOptions({
                    publicationStyleOptions
                });
            }

            // Let's see if we really need to update the Colibrio ReaderViewOptions. As we have already set the
            // publicationStyleOptions already.
            let colibrioReaderViewOptionsClone = JSON.parse(JSON.stringify(colibrioReaderViewOptions)) as IReaderViewOptions;
            delete colibrioReaderViewOptionsClone.publicationStyleOptions;
            delete viewOptions.colibrioReaderViewOptions.publicationStyleOptions;

            const viewOptionsHasChanges = !ObjectUtils.deepEqual(colibrioReaderViewOptionsClone, viewOptions.colibrioReaderViewOptions)
            if(Object.entries(viewOptions.colibrioReaderViewOptions).length > 0 && viewOptionsHasChanges) {
                this.colibrioReaderView.setOptions(viewOptions.colibrioReaderViewOptions);
            }
        }

        this._persistCurrentVanillaReaderOptionsState().then(()=>{
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('VIEW_OPTIONS_CHANGED', {viewOptions}));
        }).catch(console.warn);

    }

    getColibrioReaderPublication(): IReaderPublication {
        return this._colibrioReaderView.getReaderPublications()[0];
    }

    getVanillaReaderPublication(): VanillaReaderPublication {
        return this._vanillaReaderPublication;
    }

    getVisibleRange(): IContentLocation | null {
        return this._colibrioReaderView.getVisibleRange();
    }

    getPageProgressionTimeline(): IPageProgressionTimeline | null {
        return this._colibrioReaderView.getPageProgressionTimeline();
    }

    setAllowedGestureTypes(gestureTypes: ReaderViewGestureType[]): void {
        this._colibrioReaderView.setAllowedGestureTypes(gestureTypes);
    }

    setContentSelection(enabled: boolean): void {
        this._colibrioReaderView.setContentSelectionEnabled(enabled);
    }

    async fetchVisibleRangeData(
        navigationTree?: VanillaReaderNavigationTree,
        readingProgressionTimeline?: IVanillaReaderReadingProgressionTimeline,
    ): Promise<IVanillaReaderVisibleRangeData | undefined> {

        return this._fetchVisibleRangeData(navigationTree, readingProgressionTimeline);

    }

    async fetchContentPositionDataFromHref(href: string, baseUrl: URL): Promise<IVanillaReaderContentPositionData | undefined> {
        let colibrioReaderPubliction = this.getColibrioReaderPublication();
        if(!VanillaReaderUtils.isEpubReaderPublication(colibrioReaderPubliction)) {
            return undefined;
        }
        let contentLocation= await colibrioReaderPubliction.fetchContentLocationFromHref(href, baseUrl);
        if(!contentLocation) {
            console.warn(`VanillaReaderView.fetchContentPositionDataFromHref(), Unable to retrieve ContentLocation for "${baseUrl.href}/${href}" `);
            return;
        }
        return this.fetchContentPositionData(contentLocation);
    }

    createPageMarkerLayer(pageListData: IVanillaReaderSyntheticPageListData): void {
        this.pageMarkerLayer = new VanillaReaderAnnotationLayerPageMarkers(this, pageListData);
        this.pageMarkerLayer.setVisible(this._showPageMarkerLayer);
    }

    togglePageMarkerLayer(forceState?: boolean): void {

        if (!this.pageMarkerLayer) {
            return;
        }

        if (forceState !== undefined) {
            this.showPageMarkerLayer = forceState;
            return;
        }

        this.showPageMarkerLayer = !this.pageMarkerLayer.isVisible;
    }

    /*
    *
    * Change the current renderer for a new renderer instance that matches the `rendererName` argument.
    * Have a look at the `VanillaReaderRendererNames` enum to see all the available renderers.
    *
    * */
    changeRenderer(rendererName: string, ignoreAspectRatio: boolean | undefined): void {
        if (this._colibrioReaderView) {

            // If the selected renderer name is not set to our `RESPONSIVE` flag we explicitly set the new renderer.
            // If is set to responsive however, we let the reader view take care of things for us.
            if (rendererName !== VanillaReaderRendererNames.RESPONSIVE) {

                if (Object.values(VanillaReaderRendererNames).includes(rendererName as VanillaReaderRendererNames)) {
                    let renderer = this._colibrioReaderView.getRendererByName(rendererName);
                    if (renderer) {
                        if (rendererName != VanillaReaderRendererNames.SCROLL) {
                            // If the renderer is paginated we should take care to use the set the `disableAnimations`
                            // so that the `_useReducedMotion` value is respected by the new renderer.
                            let paginatedRendererOptions: IPaginatedRendererOptions = {
                                disableAnimations: this._initialVanillaReaderViewOptions?.useReducedMotion,
                                ignoreAspectRatio: ignoreAspectRatio,
                            };
                            renderer.setOptions(paginatedRendererOptions);
                        }
                        this._colibrioReaderView.setResponsiveRendererSelectionEnabled(false);
                        this._colibrioReaderView.setActiveRenderer(renderer);
                        this.changeRendererAspectRatio(ignoreAspectRatio);
                    } else {
                        console.error('VanillaReader.changeRenderer: Renderer name "' + rendererName + '" does not exist');
                    }
                } else {
                    console.error('VanillaReader.changeRenderer: Renderer name "' + rendererName + '" does not exist');
                }

            } else {
                // The rendererName matches`VanillaReaderRendererNames.RESPONSIVE`, this is our flag to we let Colibrio
                // find the best renderer based on the options we specified in the `_createRenderers` method.
                this._colibrioReaderView.setResponsiveRendererSelectionEnabled(true);
                this.changeRendererAspectRatio(ignoreAspectRatio);
                this.refresh(true);
            }
        } else {
            console.warn('VanillaReader.changeRenderer(): There is no _colibrioReaderView instance, is the current publication an audiobook perhaps? ');
        }
    }

    /*
    *
    * Set the `ignoreAspectRatio` specifically. This makes the `IReaderView` ignore the width and height that the
    * publication has defined and will take up all available space.
    *
    * */
    changeRendererAspectRatio(ignore: boolean = false): void {
        if (this._colibrioReaderView) {
            this.ignoreAspectRatio = ignore;

        } else {
            console.warn('VanillaReader.changeRenderer(): There is no _colibrioReaderView instance, is the current publication an audiobook perhaps? ');
        }
    }

    /**
     *
     * Refresh the `_colibrioReaderView`. You need to do this after changing view options in order for them to take effect.
     *
     * */
    refresh(force: boolean = false): void {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('VIEW_WILL_REFRESH', {}));
        this._colibrioReaderView?.refresh(force);
    }

    /*
    *
    * This utility method checks if the content that is currently visible on the screen is the first
    * content available in the publication.
    *
    * */
    async visibleRangeIsAtStartOfPublication(): Promise<boolean> {
        const visibleRange = this._colibrioReaderView?.getVisibleRange();
        const colibrioReaderPublication = this._vanillaReaderReadingSystem.getColibrioReaderPublication();
        let locator = visibleRange?.getLocator();

        if (visibleRange && locator) {
            const visibleRangeStart = visibleRange.collapseToStart();

            const firstReaderDocumentInPublication = colibrioReaderPublication?.getSpine()[0];
            const firstReaderDocumentContentLocation = await firstReaderDocumentInPublication?.fetchContentLocationForEntireDocument();
            const firstReaderDocumentStart = firstReaderDocumentContentLocation?.collapseToStart();

            return firstReaderDocumentStart?.equals(visibleRangeStart) || false;
        } else {
            console.warn('VanillaReader.readingPositionIsAtStartOfPublication(): Could not resolve a locator or visibleRange.');
            return false;
        }
    }

    /*
    *
    * This utility method checks if the content that is currently visible on the screen is the last
    * content available in the publication.
    *
    * */
    async visibleRangeIsAtEndOfPublication(): Promise<boolean> {
        const visibleRange = this._colibrioReaderView?.getVisibleRange();
        const colibrioReaderPublication = this._vanillaReaderReadingSystem.getColibrioReaderPublication();
        let locator = visibleRange?.getLocator();

        if (visibleRange && locator) {
            const visibleRangeEnd = visibleRange.collapseToEnd();

            const readerPublicationLength = colibrioReaderPublication.getSpine().length || 0;
            const lastReaderDocumentInPublication = colibrioReaderPublication.getSpine()[readerPublicationLength - 1];
            const lastReaderDocumentContentLocation = await lastReaderDocumentInPublication?.fetchContentLocationForEntireDocument();
            const lastReaderDocumentEnd = lastReaderDocumentContentLocation?.collapseToEnd();

            return lastReaderDocumentEnd?.equals(visibleRangeEnd) || false;

        } else {
            console.warn('VanillaReader.readingPositionIsAtEndOfPublication(): Could not resolve a locator or visibleRange.');
            return false;
        }
    }

    /*
    *
    * This utility method checks if the content that is currently visible is at the very start of the content document.
    * In other words that the start `IContentLocation` of the visible range is equal to the start IContentLocation` of
    * the entire document.
    *
    * */
    async visibleRangeIsAtStartOfDocument(): Promise<boolean> {
        const visibleRange = this._colibrioReaderView?.getVisibleRange();
        let locator = visibleRange?.getLocator();

        if (visibleRange && locator) {
            const visibleRangeStart = visibleRange.collapseToStart();
            const visibleRangeEnd = visibleRange.collapseToEnd();
            const visibleReaderDocument = visibleRangeEnd.getReaderDocuments()[0];
            const visibleReaderDocumentContentLocation = await visibleReaderDocument.fetchContentLocationForEntireDocument();
            const visibleReaderDocumentStart = visibleReaderDocumentContentLocation.collapseToStart();

            return visibleReaderDocumentStart.equals(visibleRangeStart);

        } else {
            console.warn('VanillaReader.readingPositionIsAtStartOfDocument(): Could not resolve a locator or visibleRange.');
            return false;
        }
    }

    /*
    *
    * This utility method checks if the content that is currently visible is at the very end of the content document.
    * In other words that the end `IContentLocation` of the visible range is equal to the end IContentLocation` of
    * the entire document.
    *
    * */
    async visibleRangeIsAtEndOfDocument(): Promise<boolean> {
        const visibleRange = this._colibrioReaderView?.getVisibleRange();
        let locator = visibleRange?.getLocator();

        if (visibleRange && locator) {
            const visibleRangeEnd = visibleRange.collapseToEnd();
            const visibleReaderDocument = visibleRangeEnd.getReaderDocuments()[0];
            const visibleReaderDocumentContentLocation = await visibleReaderDocument.fetchContentLocationForEntireDocument();
            const visibleReaderDocumentEnd = visibleReaderDocumentContentLocation.collapseToEnd();

            return visibleReaderDocumentEnd.equals(visibleRangeEnd);

        } else {
            console.warn('VanillaReader.readingPositionIsAtEndOfDocument(): Could not resolve a locator or visibleRange.');
            return false;
        }
    }

    async getReadingPositionData(): Promise<IVanillaReaderContentPositionData | undefined> {
        const readingPosition = this?.getReadingPosition();
        if (!readingPosition) {
            return;
        }

        return this.fetchContentPositionData(readingPosition);
    }

    async fetchVisibleContentRectDataForContentLocation(contentLocation: IContentLocation): Promise<IVisibleContentRectData[]> {
        if (!this._currentVisibleContentRects) {
            await this.updateAndFetchVisibleContentRects();
            if (!this._currentVisibleContentRects) {
                console.log('VanillaReaderView.fetchVisibleContentRectDataForContentLocation(), this._currentVisibleContentRects is undefined.');
                return [];
            }
        }

        let matchingVisibleContentRects = this._currentVisibleContentRects.filter((rect: IVisibleContentRect) => {
            return rect.contentLocation.intersects(contentLocation);
        });

        if (matchingVisibleContentRects.length === 0) {
            let nextContentRect = this._currentVisibleContentRects.find((rect: IVisibleContentRect) => {
                return rect.contentLocation.isAfter(contentLocation);
            });

            matchingVisibleContentRects = nextContentRect ? [nextContentRect] : [];
        }

        return matchingVisibleContentRects.map(rect => rect.toJSON());

    }

    async updateAndFetchVisibleContentRects(): Promise<IVisibleContentRect[]> {

        this._currentVisibleContentRects = undefined;

        let contentPositionClientRects: IVisibleContentRect[] = [];

        const pages = this._colibrioReaderView.getVisiblePages();

        for (const page of pages) {
            let locationRectsResult = (await this._colibrioReaderView.fetchRectsForVisibleContent(page, undefined, {
                includeNodeData: true,
            }));

            if (locationRectsResult.rects.length > 0) {
                contentPositionClientRects.push(...locationRectsResult.rects);
            }
        }

        this._currentVisibleContentRects = contentPositionClientRects;

        return contentPositionClientRects;
    }

    async fetchContentPositionData(contentLocation: IContentLocation): Promise<IVanillaReaderContentPositionData | undefined> {
        if (!contentLocation) {
            return undefined;
        }

        const navigationItems: IFetchNavigationItemReferencesResult | undefined = await contentLocation.fetchNavigationItemReferences({
            greedy: true,
            collectionTypes: [
                NavigationCollectionType.TOC,
                NavigationCollectionType.LANDMARKS,
                NavigationCollectionType.PAGE_LIST,
            ],
        });

        const navigationItemsData: IVanillaReaderNavigationItem[] | undefined = navigationItems.getItemsInRange()?.map((item: IReaderPublicationNavigationItemReference): IVanillaReaderNavigationItem => {
            return {
                title: item.getNavigationItem().getTextContent(),
                collectionType: item.getNavigationCollection().getType(),
                children: [],
                locator: item.getNavigationItem().getLocator()?.toString(),
            };
        });

        const contentBlockTargets = await contentLocation.fetchContentBlockTargets(ContentBlockTargetFetchMode.INTERSECTING_OR_NEAREST_AFTER);

        let contentPositionClientRects = (await this.fetchVisibleContentRectDataForContentLocation(contentLocation)) || [];
        let syntheticPageData = await this._vanillaReaderPublication.pageList?.fetchPagesForLocator(contentLocation.getLocator())

        return {
            publicationId: this._vanillaReaderPublication.id,
            documentIndex: contentLocation.getReaderDocuments()[0].getSourceContentDocument().getIndexInSpine(),
            locator: contentLocation.getLocator().toString(),
            contentBlockData: contentBlockTargets.length > 0 ?
                contentBlockTargets[0].getContentBlock().toSerializableData({
                    createLocators: true,
                    recursive: false,
                }) :
                undefined,
            navItemData: navigationItemsData,
            timestamp: Date.now(),
            contentRects: contentPositionClientRects,
            syntheticPageData
        };

    }

    renderBookmarks(bookmarkData: IVanillaReaderBookmarkData[]): void {
        this.bookmarksLayer?.loadBookmarks(bookmarkData);
    }

    renderHighlights(highlightData: IVanillaReaderHighlightData[]): void {
        this.highlightsLayer?.loadHighlights(highlightData);
    }

    /*
    *
    * This property controls if the `VanillaReader` should take care to not use too many UI animations.
    * As you can see in the setter we toggle off the renderer transition animations if `useReducedMotion` is set to true.
    * */

    public get useReducedMotion(): boolean {
        return this._useReducedMotion;
    }

    public set useReducedMotion(useReducedMotion: boolean) {

        if(this.useReducedMotion === useReducedMotion) {
            return;
        }

        this._useReducedMotion = useReducedMotion;
        if (this._colibrioReaderView) {
            // Casting to unknown because otherwise TS would not narrow the type from IRenderer
            const activeRenderer = this.colibrioReaderView.getActiveRenderer() as unknown;
            if (VanillaReaderView.isPaginatedRenderer(activeRenderer)) {
                activeRenderer.setOptions({
                    disableAnimations: useReducedMotion,
                });
            }
        }

        if (this._onReducedMotionValueChangedCallback) {
            this._onReducedMotionValueChangedCallback(useReducedMotion);
        }

        this.refresh(true);

    }

    public get scrollOverflowingTables(): boolean {
        return this._scrollOverflowingTables;
    }

    public set scrollOverflowingTables(scrollOverflowingTables: boolean) {

        if(this._scrollOverflowingTables === scrollOverflowingTables) {
            return;
        }

        this._scrollOverflowingTables = scrollOverflowingTables;
        let readerPublication = this.getColibrioReaderPublication();

        if(VanillaReaderUtils.isEpubReaderPublication(readerPublication)) {
            let activeOptions = readerPublication.getOptions();
            let blacklistedSelectors = activeOptions.reflowOptions.plugins.fragmentableElements.blacklistSelectors;

            if(scrollOverflowingTables === true && !blacklistedSelectors.includes('table')) {
                blacklistedSelectors.push('table');
                activeOptions.reflowOptions.plugins.fragmentableElements.blacklistSelectors = blacklistedSelectors;
            } else {
                let updatedBlacklist = blacklistedSelectors.filter((selector)=>{
                    return selector !== 'table'
                });
                activeOptions.reflowOptions.plugins.fragmentableElements.blacklistSelectors = updatedBlacklist;
            }

            this._vanillaReaderPublication.setPublicationOptions(activeOptions);

            this.refresh(true);
        }
    }

    public get ignoreAspectRatio(): boolean {
        return this._ignoreAspectRatio;
    }

    public set ignoreAspectRatio(preference: boolean) {

        if(preference === this.ignoreAspectRatio) {
            return;
        }

        this._ignoreAspectRatio = preference;
        if (this._colibrioReaderView) {
            this.colibrioReaderView.getActiveRenderer()?.setOptions({
                ignoreAspectRatio: preference,
            });
        }

        if (this._onIgnoreAspectRatioValueChangedCallback) {
            this._onIgnoreAspectRatioValueChangedCallback(preference);
        }

        this.refresh(true);

    }

    public get showPageMarkerLayer(): boolean {
        return this._showPageMarkerLayer;
    }

    public set showPageMarkerLayer(showPageMarkerLayer: boolean) {

        if(this._showPageMarkerLayer === showPageMarkerLayer) {
            return;
        }

        this._showPageMarkerLayer = showPageMarkerLayer;
        this.pageMarkerLayer?.setVisible(showPageMarkerLayer);
    }

    public get activeRendererName(): VanillaReaderRendererNames | undefined {
        let activeRendererName = this.isResponsiveRendererSelectionEnabled() ? VanillaReaderRendererNames.RESPONSIVE : this.getActiveRenderer()?.getName();
        return  activeRendererName as VanillaReaderRendererNames;
    }

    resetViewTransformation(): void {
        const colibrioTransformManager: IReaderViewTransformManager = this._colibrioReaderView.getTransformManager();
        colibrioTransformManager?.removeActiveTransform().catch(console.warn);
    }

    async zoomToClientRect(zoomRect: DOMRect, animate: boolean = true): Promise<IVanillaReaderTransformData | null> {
        const colibrioTransformManager: IReaderViewTransformManager = this._colibrioReaderView.getTransformManager();
        if (!colibrioTransformManager.canTransform()) {
            console.warn('VanillaReaderView.zoomToClientRect(), ReaderView is not in Transform state.');
            return null;
        }

        let transformedPublicationClientRects = this.getVisiblePagesBoundingClientRects();
        let publicationViewportScale = this._getPublicationViewportScale(this._initialPublicationViewportRects, transformedPublicationClientRects);

        this._setActiveTransformData({
            transformData: this.activeTransformData?.transformData,
            visiblePageClientRects: this.activeTransformData?.visiblePageClientRects || [],
            zoomTargetClientRect: zoomRect,
            publicationViewportScale
        });

        await colibrioTransformManager.zoomToClientRect(zoomRect, {
            animationOptions: {
                durationMs: animate ? this._defaultAnimationDuration : 0,
                easing: TransformEasing.MATERIAL_DECELERATION_CURVE
            }
        });

        let transformData = colibrioTransformManager.getActiveTransform() || undefined;

        transformedPublicationClientRects = this.getVisiblePagesBoundingClientRects();
        publicationViewportScale = this._getPublicationViewportScale(this._initialPublicationViewportRects, transformedPublicationClientRects);

        this._setActiveTransformData({
            transformData,
            visiblePageClientRects:  this.getVisiblePagesBoundingClientRects(),
            zoomTargetClientRect: zoomRect,
            publicationViewportScale
        });

        return this.activeTransformData;
    }

    async zoomToPublicationViewportRect(rect: IRect, unit: LengthUnit, options: ITransformRectZoomOptions): Promise<void> {
        const colibrioTransformManager: IReaderViewTransformManager = this._colibrioReaderView.getTransformManager();
        if (!colibrioTransformManager) {
            console.warn('VanillaReaderView.zoomToPublicationViewportRect(), ReaderView is not in Transform state.');
            return undefined;
        }

        let transformedPublicationClientRects = this.getVisiblePagesBoundingClientRects();
        let publicationViewportScale = this._getPublicationViewportScale(this._initialPublicationViewportRects, transformedPublicationClientRects);

        this._setActiveTransformData({
            transformData: this.activeTransformData?.transformData,
            visiblePageClientRects: this.activeTransformData?.visiblePageClientRects || [],
            zoomTargetClientRect: rect,
            publicationViewportScale
        });

        let domRect = new DOMRect(rect.x, rect.y, rect.width, rect.height);

        await colibrioTransformManager.zoomToPublicationViewportRect(domRect, unit,{
                animationOptions: {
                durationMs: options.animationOptions?.durationMs ?? this._defaultAnimationDuration,
                easing: TransformEasing.MATERIAL_DECELERATION_CURVE
            }
        });

        let transformData = colibrioTransformManager.getActiveTransform() || undefined;

        transformedPublicationClientRects = this.getVisiblePagesBoundingClientRects();
        publicationViewportScale = this._getPublicationViewportScale(this._initialPublicationViewportRects, transformedPublicationClientRects);

        this._setActiveTransformData({
            transformData,
            visiblePageClientRects:  this.getVisiblePagesBoundingClientRects(),
            zoomTargetClientRect: rect,
            publicationViewportScale
        });
    }

    async zoomToPublicationViewportPosition(x: number, y: number, unit: LengthUnit, scale: number, options: ITransformAnimationOptions): Promise<ITransformData | null> {
        const colibrioTransformManager: IReaderViewTransformManager = this._colibrioReaderView.getTransformManager();
        if (!colibrioTransformManager) {
            console.warn('VanillaReaderView.zoomToPublicationViewportPosition(), ReaderView is not in Transform state.');
            return null;
        }

        await colibrioTransformManager.zoomToPublicationViewportPosition({unit, value:x}, {unit, value:y}, scale,options);
        return colibrioTransformManager.getActiveTransform();
    }

    async zoomToLevel(zoomLevel: number, animate: boolean = true): Promise<ITransformData | null> {
        const colibrioTransformManager: IReaderViewTransformManager = this._colibrioReaderView.getTransformManager();
        if (!colibrioTransformManager) {
            console.warn('VanillaReaderView.zoomToClientRect(), ReaderView is not in Transform state.');
            return null;
        }
        let viewElementBoundingBox = this._readerViewElement.getBoundingClientRect();
        let clientX = viewElementBoundingBox.left + viewElementBoundingBox.width / 2;
        let clientY = viewElementBoundingBox.top + viewElementBoundingBox.height / 2;

        await colibrioTransformManager.zoomToClientPosition(clientX, clientY, zoomLevel, {
            durationMs: animate ? this._defaultAnimationDuration : 0,
            easing: TransformEasing.MATERIAL_DECELERATION_CURVE
        });

        return colibrioTransformManager.getActiveTransform();
    }

    getPublicationViewportBoundingClientRect(): IRect | undefined {
        let visiblePages = this._colibrioReaderView.getVisiblePages();

        if(visiblePages.length < 1) {
            console.warn("VanillaReaderView.getPublicationViewportBoundingClientRect(), IReaderView.getVisiblePages returned an empty array.");
            return;
        }

        let minX: number = Infinity;
        let minY: number= Infinity ;
        let maxX: number = 0;
        let maxY: number = 0;

        for (let page of visiblePages) {
            let rect = page.getBoundingClientRect();
            if(!rect) {
                console.warn('VanillaReaderView.getPublicationViewportBoundingClientRect(), page.getBoundingClientRect() returned null.')
                continue;
            }
            minX = Math.min(rect.x,minX);
            minY = Math.min(rect.y,minY);
            maxY = Math.max(Math.abs(rect.x) + rect.width,maxY);
            maxX = Math.max(rect.height,maxX);
        }

        if(minX === Infinity || minY === Infinity) {
            console.warn("VanillaReaderView.getPublicationViewportBoundingClientRect(), Unable to get bounding client rects from visible pages.");
            return;
        }

        return {
            x: minX,
            y: minY,
            width: maxY,
            height: maxX
        }
    }

    getVisiblePagesBoundingClientRects(): IRect[] {
        let visiblePages = this._colibrioReaderView.getVisiblePages();
        let rects: IRect[] = [];

        if(visiblePages.length < 1) {
            console.warn("VanillaReaderView.getVisiblePagesBoundingClientRects(), IReaderView.getVisiblePages returned an empty array.");
            return [];
        }

        for (let page of visiblePages) {
            let rect = page.getBoundingClientRect();
            if(!rect) {
                console.warn('VanillaReaderView.getVisiblePagesBoundingClientRects(), page.getBoundingClientRect() returned null.')
                continue;
            }
            rects.push(rect);
        }

        return rects;
    }

    /**
     *
     * PRIVATE METHODS
     *
     * */

    private _getPublicationViewportScale(initialRects: IRect[], transformedRects: IRect[]): number {
        return  initialRects.length >= 0 && transformedRects.length > 0 ?
            transformedRects[0].width / initialRects[0].width:
            1;
    }

    private _setActiveTransformData(data: IVanillaReaderTransformData | undefined): IVanillaReaderTransformData {
        this._activeTransformData = {
            transformData: data?.transformData,
            visiblePageClientRects: data?.visiblePageClientRects || [],
            zoomTargetClientRect: data?.zoomTargetClientRect,
            publicationViewportScale: data?.publicationViewportScale || 1
        }
        return this.activeTransformData;
    }

    private async _fetchVisibleRangeData(
        navigationTree?: VanillaReaderNavigationTree,
        readingProgressionTimeline?: IVanillaReaderReadingProgressionTimeline,
    ): Promise<IVanillaReaderVisibleRangeData | undefined> {

        const visibleRange = this._colibrioReaderView?.getVisibleRange();
        let locator = visibleRange?.getLocator();

        if (!visibleRange || !locator) {
            return undefined;
        }
        let timelineRange: IIntegerRange | undefined;
        let readingProgression: number | undefined;
        let rangeTimelineStartPosition: number | undefined;
        let rangeTimelineEndPosition: number | undefined;

        if (readingProgressionTimeline) {
            rangeTimelineStartPosition = await readingProgressionTimeline.fetchTimelinePosition(visibleRange.collapseToStart().getLocator());
            rangeTimelineEndPosition = await readingProgressionTimeline.fetchTimelinePosition(visibleRange.collapseToEnd().getLocator());
            readingProgression = rangeTimelineStartPosition ?
                rangeTimelineStartPosition / readingProgressionTimeline.getLength() :
                undefined;

            if (rangeTimelineStartPosition && rangeTimelineEndPosition) {
                timelineRange = {
                    start: rangeTimelineStartPosition,
                    end: rangeTimelineEndPosition,
                };
            }
        }

        let navItems: IVanillaReaderNavigationItem[] = [];

        let navItemRefs = await navigationTree?.fetchItemsInVisibleRange([
            NavigationCollectionType.TOC,
            NavigationCollectionType.PAGE_LIST,
            NavigationCollectionType.LANDMARKS,
        ], true, this);

        navItemRefs?.forEach((ref: IReaderPublicationNavigationItemReference) => {
            navItems.push({
                locator: ref.getNavigationItem()?.getLocator()?.toString(),
                children: [],
                collectionType: ref.getNavigationCollection().getType(),
                title: ref.getNavigationItem().getTextContent(),
            });

        });

        // Check if the start / of the visible range is at the start or end of the reader document, and if so send
        // events.

        // If the visible range has changed while we were fetching the ContentLocation we'll get a new event with more up-to-date data, so we can just return here.
        if (visibleRange !== this._colibrioReaderView?.getVisibleRange()) {
            return undefined;
        }

        let publicationIsAtStart = await this.visibleRangeIsAtStartOfPublication();
        let publicationIsAtEnd = await this.visibleRangeIsAtEndOfPublication();

        let documentIsAtStart = await this.visibleRangeIsAtStartOfDocument();
        let documentIsAtEnd = await this.visibleRangeIsAtEndOfDocument();
        let visibleContentRects: IVisibleContentRectData[] = [];

        for (const page of this._colibrioReaderView.getVisiblePages()) {
            visibleContentRects = await this.fetchVisibleContentRectDataForContentLocation(page.getRangeContentLocation());
        }

        let contentResolverForVisibleContent = await visibleRange.fetchContentResolver();
        let visibleTextContent: string = contentResolverForVisibleContent.getTextContent();

        return {
            locator,
            timelinePosition: rangeTimelineStartPosition,
            timelineRange,
            readingProgression,
            bookmarks: this.bookmarksLayer?.getBookmarksInVisibleRange() || [],
            highlights: this.highlightsLayer?.getHighlightsInVisibleRange() || [],
            navItems: navItems || [],
            documentIsAtStart,
            documentIsAtEnd,
            publicationIsAtStart,
            publicationIsAtEnd,
            visibleTextContent,
            visibleContentRects,
        };

    }

    /**
     *
     * The _persistCurrentVanillaReaderOptionsState method retrieves the current active view options and persists them using the
     * `IVanillaReaderOptionsDataStore` instance.
     *
     * */
    private _persistCurrentVanillaReaderOptionsState(): Promise<void> {
        const viewOptions = this.getCurrentVanillaReaderViewOptionsState();
        return this._vanillaReaderOptionsDataStore.setVanillaReaderOptionsData({
            dateCreated: Date.now(),
            viewOptions,
        });
    }

    /*
     *
     * _createRenderers creates the default `IReaderView` instance for the Vanilla Reader. It also sets some default
     * view settings, and adds a number of renderers.
     *
     * For audiobooks that have no traditional view, please have a look at the _createAudiobookView method.
     *
     * */
    private _createRenderers(colibrioReaderView: IReaderView): void {

        // Grab the background color to use for the renderer from the initial options
        let rendererBackgroundColor: string | undefined = this._activePalette?.backgroundLight;

        /*
        *
        * ## A note about Renderers
        *
        * How a book is presented to the user is determined by what kind of `IRenderer` type that is currently set as
        * active in the `IReaderView`.
        *
        * Each type of renderer has different options that affects properties such as if the renderer should keep the
        * aspect ratio defined in the publication, if it should animate to the next / previous page, if it should show
        * a shadow behind the page or spread etc. etc.
        *
        * A handy feature that is also supported by the reader view is that it can choose a fitting renderer based on
        * so called `ResponsiveViewRule` objects. These are defined using the same syntax as CSS Media Queries and
        * saves you the trouble of manually switching renderer from the UI.
        *
        * */

        const colibrioFlipBookRenderer = new FlipBookRenderer({
            name: VanillaReaderRendererNames.FLIPBOOK,
            ignoreAspectRatio: this._initialVanillaReaderViewOptions?.ignoreAspectRatio,
            disableAnimations: this._initialVanillaReaderViewOptions?.useReducedMotion,
            pageBackgroundColor: rendererBackgroundColor,
        });

        const colibrioStackRenderer = new StackRenderer({
            name: VanillaReaderRendererNames.STACK,
            pageBackgroundColor: rendererBackgroundColor,
        });

        const colibrioStackRendererMobile = new StackRenderer();
        colibrioStackRendererMobile.setOptions({
            name: VanillaReaderRendererNames.STACK,
            showRendererBackgroundShadow: false,
            ignoreAspectRatio: true,
            disableAnimations: this._initialVanillaReaderViewOptions?.useReducedMotion,
            pageBackgroundColor: rendererBackgroundColor,
        });
        const colibrioSwipeRendererSingle = new SinglePageSwipeRenderer({
            name: VanillaReaderRendererNames.SWIPE_SINGLE,
            ignoreAspectRatio: this._initialVanillaReaderViewOptions?.ignoreAspectRatio,
            disableAnimations: this._initialVanillaReaderViewOptions?.useReducedMotion,
            pageBackgroundColor: rendererBackgroundColor,
        });
        const colibrioSwipeRendererSpread = new SpreadSwipeRenderer({
            name: VanillaReaderRendererNames.SWIPE_SPREAD,
            ignoreAspectRatio: this._initialVanillaReaderViewOptions?.ignoreAspectRatio,
            disableAnimations: this._initialVanillaReaderViewOptions?.useReducedMotion,
            pageBackgroundColor: rendererBackgroundColor,
        });

        const colibrioScrollRenderer = new SingleDocumentScrollRenderer({
            name: VanillaReaderRendererNames.SCROLL,
            ignoreAspectRatio: this._initialVanillaReaderViewOptions?.ignoreAspectRatio,
            readingAreaOffsetTop: {
                value: 5,
                unit: LengthUnit.PERCENT,
            },
            pageBackgroundColor: rendererBackgroundColor,
        });

        // Use the flip book renderer for all screens wider than 600px that are in landscape mode
        colibrioReaderView.addRenderer(colibrioFlipBookRenderer, new ResponsiveViewRule('(orientation: landscape) and (min-width: 600px)'));

        // Use the stack renderer for all screens wider than 450px, but narrower than 600px
        colibrioReaderView.addRenderer(colibrioStackRenderer, new ResponsiveViewRule('(min-width: 450px)'));

        // Use the stack renderer without background shadow, and ignoring the publication aspect ratio for all screens
        // narrower than 450px
        colibrioReaderView.addRenderer(colibrioStackRendererMobile, new ResponsiveViewRule('(max-width: 450px)'));

        colibrioReaderView.addRenderer(colibrioSwipeRendererSingle);
        colibrioReaderView.addRenderer(colibrioSwipeRendererSpread);
        colibrioReaderView.addRenderer(colibrioScrollRenderer);

    }

    private _setupBookmarkLayerCallbacks(bookmarksLayer: VanillaReaderAnnotationLayerBookmarks): void {

        bookmarksLayer.onBookmarkClick((ev: IReaderViewAnnotationMouseEngineEvent) => {
            let locator = ev.annotation.getLocator();
            if (locator) {
                let bookmark = bookmarksLayer.getBookmark(locator.toString());
                if (bookmark) {
                    VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('BOOKMARK_CLICKED', {bookmark}));
                }
            }
        });

        bookmarksLayer.onBookmarkEnterView((bookmark: IVanillaReaderBookmarkData) => {
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('BOOKMARK_IN_VISIBLE_RANGE', {bookmark}));
        });

        bookmarksLayer.onBookmarkExitView((bookmark: IVanillaReaderBookmarkData) => {
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('BOOKMARK_OUTSIDE_VISIBLE_RANGE', {bookmark}));
        });
    }

    private _setupHighlightLayerCallbacks(highlightsLayer: VanillaReaderAnnotationLayerHighlights): void {

        highlightsLayer.onHighlightContextMenu((event: IReaderViewAnnotationMouseEngineEvent) => {
            let highlight = event.annotation.getCustomData();
            if (highlight && VanillaReaderView.isVanillaReaderHighlightData(highlight)) {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('HIGHLIGHT_CLICKED', {highlight}));
            }
        });

        highlightsLayer.onHighlightAdded(() => {
            this._colibrioReaderView?.clearContentSelection();
        });

        highlightsLayer.onHighlightEnterView((highlight: IVanillaReaderHighlightData) => {
            if (highlight) {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('HIGHLIGHT_IN_VISIBLE_RANGE', {highlight}));
            }
        });

        highlightsLayer.onHighlightExitView((highlight: IVanillaReaderHighlightData) => {
            if (highlight) {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('HIGHLIGHT_OUTSIDE_VISIBLE_RANGE', {highlight}));
            }
        });
    }

    private _event_visibleContentRendered = async () => {
        let visibleRangeData = await this.fetchVisibleRangeData(this._vanillaReaderPublication.navigationTree, this._vanillaReaderPublication.progressionTimeline);

        VanillaReaderEventBus.dispatchEvent(
            VanillaReaderEventBus.createEvent(
                'VISIBLE_CONTENT_RENDERED', {
                    visibleRangeData,
                },
            ));
    }

    private _event_visiblePagesChanged = async () => {

        let visiblePagesBoundingClientRects = this.getVisiblePagesBoundingClientRects();

        this._initialPublicationViewportRects = visiblePagesBoundingClientRects;

        if(visiblePagesBoundingClientRects) {
            VanillaReaderEventBus.dispatchEvent(
                VanillaReaderEventBus.createEvent('PUBLICATION_VIEWPORT_CHANGED', {
                    visiblePageClientRects: visiblePagesBoundingClientRects
                })
            );
        }

        let visibleRangeData = await this.fetchVisibleRangeData(this._vanillaReaderPublication.navigationTree, this._vanillaReaderPublication.progressionTimeline);

        VanillaReaderEventBus.dispatchEvent(
            VanillaReaderEventBus.createEvent(
                'VISIBLE_PAGES_CHANGED', {
                    visibleRangeData,
                },
            ));
    }
    private static isVanillaReaderHighlightData(customData: any): customData is IVanillaReaderHighlightData {
        return 'selectionText' in customData;
    }

    private static isPaginatedRenderer(activeRenderer: any): activeRenderer is StackRenderer | FlipBookRenderer | SinglePageSwipeRenderer | SpreadSwipeRenderer {
        return activeRenderer instanceof StackRenderer ||
            activeRenderer instanceof FlipBookRenderer ||
            activeRenderer instanceof SinglePageSwipeRenderer ||
            activeRenderer instanceof SpreadSwipeRenderer;
    }
}
